import type { IDisposable } from '.'
import { DisposableBase } from './DisposableBase'
import { TimeoutController } from './TimeoutController'

export class AbortControllerManager implements IDisposable {
    private _controllerCollection: Record<string, AbortController> = {}
    private defaultTimeout = 10_000

    private createDefaultAbortController(timeout: number) {
        return new TimeoutController(timeout)
    }

    timeout? = 10_000

    getByKey(controllerKey: string, factory: () => AbortController) {
        const controller = this._controllerCollection[controllerKey] ?? factory()

        this._controllerCollection[controllerKey] = controller

        return controller
    }

    abort(controllerKey: string) {
        const controller = this._controllerCollection[controllerKey]

        if (controller) {
            controller.abort()
            delete this._controllerCollection[controllerKey]
        }
    }

    abortAfter(timeout: number, controllerKey: string) {
        const controller = this._controllerCollection[controllerKey]

        if (controller && controller instanceof TimeoutController) {
            controller.abortAfter(timeout)
        }
    }

    reset(controllerKey: string, factory?: () => AbortController) {
        if (!factory) {
            factory = () => this.createDefaultAbortController(this.timeout ?? this.defaultTimeout)
        }

        const controller = this._controllerCollection[controllerKey]

        if (controller) {
            this.abort(controllerKey)
        }

        return this.getByKey(controllerKey, factory)
    }

    dispose() {
        for (const controllerKey in this._controllerCollection) {
            const controller = this._controllerCollection[controllerKey]
            controller.abort()
        }
    }
}

export class StoreBase extends DisposableBase {
    protected readonly abortManager = new AbortControllerManager()

    constructor() {
        super()

        this._disposers.push(() => {
            this.abortManager.dispose()
        })
    }

    protected createDefaultAbortController(timeout = 20_000) {
        return new TimeoutController(timeout)
    }
}
